import { useEffect, useRef, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { useCan } from "../lib/auth";

/**
 * Sol menü.
 *
 * G-13'ün KAPANDIĞI YER: eski `sidebar.php`'de hiç rol kontrolü yoktu — üretim
 * personeli "Sipariş" ve "Satın Alma" linklerini görüyor, tıklayınca login'e
 * atılıyordu. Artık menü claim'den türetilir: göremediği link görünmez.
 *
 * Görünürlük tablosu: 06-guvenlik/04-yetki-matrisi.md §6 ile birebir.
 * NOT: Menü gizlemek bir güvenlik önlemi DEĞİLDİR; koruma Rules + CF'tedir.
 */

interface Perms {
  isBoss: boolean;
  isStaff: boolean;
  seeCost: boolean;
  seeFinance: boolean;
}

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** true dönerse menüde görünür. */
  visible: (p: Perms) => boolean;
  /** "/" için tam eşleşme gerekir. */
  end?: boolean;
}

interface NavSection {
  title: string | null;
  items: NavItem[];
}

/* ── İkonlar (eski sidebar.php'deki Heroicons yolları — tanıdık kalsın) ── */

function Icon({ children, filled = false }: { children: ReactNode; filled?: boolean }) {
  return (
    <svg
      className="h-5 w-5 shrink-0 text-gray-500 transition-colors group-hover:text-white group-[.active]:text-primary-300"
      fill={filled ? "currentColor" : "none"}
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

// key: aynı ikonda iki path olduğunda React uyarı vermesin diye.
const P = (d: string) => <path key={d.slice(0, 16)} strokeLinecap="round" strokeLinejoin="round" d={d} />;

const IconHome = <Icon>{P("M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25")}</Icon>;
const IconOrders = <Icon>{P("M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z")}</Icon>;
const IconBox = <Icon>{P("M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5")}</Icon>;
const IconFolder = <Icon>{P("M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z")}</Icon>;
const IconChart = <Icon>{P("M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75")}</Icon>;
const IconProduct = <Icon>{P("M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z")}{P("M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z")}</Icon>;
const IconCart = <Icon>{P("M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z")}</Icon>;
const IconScissors = <Icon>{P("M7.848 8.25l1.536.887M7.848 8.25a3 3 0 11-5.196-3 3 3 0 015.196 3zm1.536.887a2.165 2.165 0 011.083 1.839c.005.351.054.695.14 1.024M9.384 9.137l2.077 1.199M7.848 15.75l1.536-.887m-1.536.887a3 3 0 11-5.196 3 3 3 0 015.196-3zm1.536-.887a2.165 2.165 0 001.083-1.838c.005-.352.054-.695.14-1.025m-1.223 2.863l2.077-1.199m0-3.328a4.323 4.323 0 012.068-1.379l5.325-1.628a4.5 4.5 0 012.48-.044l.803.215-7.794 4.5m-2.882-1.664A4.33 4.33 0 0010.607 12m3.736 0l7.794 4.5-.802.215a4.5 4.5 0 01-2.48-.043l-5.326-1.629a4.324 4.324 0 01-2.068-1.379M14.343 12l-2.882 1.664")}</Icon>;
const IconWrench = <Icon>{P("M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z")}</Icon>;
const IconExcel = <Icon>{P("M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z")}</Icon>;
const IconBell = <Icon>{P("M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0")}</Icon>;
const IconUsers = <Icon>{P("M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0z")}</Icon>;
const IconCog = <Icon>{P("M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z")}{P("M15 12a3 3 0 11-6 0 3 3 0 016 0z")}</Icon>;
const IconShield = <Icon>{P("M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z")}</Icon>;
// Finans sekmesi — banknot (Heroicons "banknotes"), diğer ikonlarla aynı aile.
const IconMoney = <Icon>{P("M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 12a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V12zm-12 0h.008v.008H6V12z")}</Icon>;

/* ── Menü tanımı — yetki matrisi §6 ── */

const SECTIONS: NavSection[] = [
  {
    title: null,
    items: [
      { to: "/", label: "Dashboard", icon: IconHome, visible: () => true, end: true },
      { to: "/orders", label: "Siparişler", icon: IconOrders, visible: () => true },
      /**
       * ★ MENÜDEN KALDIRILAN ÜÇ SAYFA (iş sahibi kararı, 2026-08-31):
       *   · "İptal / İade"    (/orders/cancelled)
       *   · "Kesim Listesi"   (/cutting-list)
       *   · "Stok Siparişi"   (/stock-orders)
       *
       * ROTALAR BİLEREK DURUYOR, yalnızca menü satırları silindi. Sebebi ölçüldü:
       *   · `/cutting-list` üç yerden link alıyor — `Production.tsx:847`,
       *     `ProductionDetail.tsx:588`, `Notifications.tsx:47`. Rota silinseydi
       *     atölyenin üretimden kesim listesine geçiş akışı kırılırdı.
       *   · `/stock-orders` Dashboard'daki "açık stok siparişi" kartından link
       *     alıyor (`maintenance/metrics.ts:488`) ve canlıda 20 AÇIK kayıt var;
       *     rota silinseydi o kayıtlar hiçbir ekrandan görülemez olurdu.
       *   · `/orders/cancelled` başka hiçbir yerden linklenmiyor; yine de rota
       *     bırakıldı ki eski yer imleri 404 vermesin.
       * Menüden çıkarmak GÖRÜNÜRLÜĞÜ azaltır, YETENEĞİ kaldırmaz.
       */
      // Manuel sipariş (çekim/numune/iç kullanım/stok) — üretim rolü görmez.
      { to: "/orders/manual", label: "Manuel Sipariş", icon: IconOrders, visible: (p) => p.isStaff },
      { to: "/production", label: "Üretim", icon: IconFolder, visible: () => true },
      { to: "/leg-list", label: "Ayak Listesi", icon: IconScissors, visible: () => true },
      { to: "/after-sales", label: "SSH", icon: IconBox, visible: () => true },
      { to: "/inventory", label: "Stoklar", icon: IconChart, visible: () => true },
    ],
  },
  {
    title: "Katalog",
    items: [
      { to: "/products", label: "Ürünler", icon: IconProduct, visible: () => true },
      // Parça (koli) yönetimi: eski koli_yonetimi.php üretimi dışlıyordu.
      { to: "/components", label: "Parça Yönetimi", icon: IconWrench, visible: (p) => p.isStaff },
      // Satın alma: eski procurement.php ofise açıktı → costView.
      { to: "/procurement", label: "Satın Alma", icon: IconCart, visible: (p) => p.seeCost },
    ],
  },
  {
    title: "Finans",
    items: [
      /**
       * FİNANS SEKMESİ — YALNIZCA İŞ SAHİBİ.
       *
       * ★ KOŞUL ÜÇ YERDE DE AYNI: burada `isBoss && seeFinance`, rotada
       * `<RequireRole roles={["boss"]} financeView>`, sunucuda
       * `assertRole(caller,["boss"]) + assertFinanceView(caller)`.
       * `reports/profit` yolunda bu üç yer üç FARKLI koşul soruyordu ve
       * `financeView` verilmiş bir ofis kullanıcısı menüde göründüğü hâlde
       * tıklayınca 403 alıyordu — menü olmayan bir yetki vaat ediyordu.
       *
       * ⚠ Menü gizlemek GÜVENLİK ÖNLEMİ DEĞİLDİR; koruma Rules + callable
       * guard'ındadır. Eski sistemin G-08 açığı tam olarak "yalnızca HTML'de
       * gizlemek"ti (`ajax_financialdashboard.php` herkese açıktı).
       */
      { to: "/finance", label: "Finans", icon: IconMoney, visible: (p) => p.isBoss && p.seeFinance },
    ],
  },
  {
    title: "Raporlar",
    items: [
      // Hangi üründen kaç adet üretildi — para içermez, üç rol de görür.
      { to: "/reports/production", label: "Üretim Raporu", icon: IconChart, visible: () => true },
      // Kârlılık ve masif panel Excel'i: eski products_single.php gibi YALNIZCA patron.
      //
      // ★ KOŞUL SUNUCUDAKİYLE AYNI OLMAK ZORUNDA: `reports/profit.ts:124-126`
      // hem `role === "boss"` hem `financeView` arıyor. Burada yalnızca
      // `seeFinance` sorulduğu için, `financeView` verilmiş bir OFİS kullanıcısı
      // menüde bu iki satırı görüyor ama tıklayınca 403 paneline düşüyordu —
      // menü, olmayan bir yetkiyi vaat ediyordu.
      { to: "/reports/profit", label: "Kârlılık Tablosu", icon: IconExcel, visible: (p) => p.isBoss && p.seeFinance },
    ],
  },
  {
    title: "Yönetim",
    items: [
      { to: "/notifications", label: "Bildirimler", icon: IconBell, visible: () => true },
      { to: "/users", label: "Kullanıcı Yönetimi", icon: IconUsers, visible: (p) => p.isBoss },
      { to: "/settings", label: "Ayarlar", icon: IconCog, visible: (p) => p.isBoss },
      { to: "/audit-log", label: "Denetim Kaydı", icon: IconShield, visible: (p) => p.isBoss },
    ],
  },
];

/**
 * ★ 2026 YENİLEMESİ: koyu (mürekkep) menü, tek vurgu rengi. Etkin sayfa solda
 * ince çivit çizgi + hafif aydınlık zeminle belirir; eski parlak mavi blok
 * yerine göz yormayan, net bir hiyerarşi.
 */
const LINK_BASE =
  "group relative flex min-h-[44px] items-center gap-x-3 rounded-lg px-2.5 py-2 text-sm font-medium leading-6 text-gray-400 transition-colors hover:bg-white/5 hover:text-white lg:min-h-0";
const LINK_ACTIVE =
  "active bg-white/10 text-white before:absolute before:inset-y-2 before:-left-2 before:w-[3px] before:rounded-full before:bg-primary-400";

function NavList({ perms, onNavigate }: { perms: Perms; onNavigate?: () => void }) {
  return (
    <nav className="flex flex-1 flex-col" aria-label="Ana menü">
      <ul role="list" className="flex flex-1 flex-col gap-y-5">
        {SECTIONS.map((section, i) => {
          const items = section.items.filter((it) => it.visible(perms));
          if (items.length === 0) return null;
          return (
            <li key={section.title ?? `section-${i}`}>
              {section.title ? (
                <div className="px-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  {section.title}
                </div>
              ) : null}
              <ul role="list" className="-mx-2 mt-1.5 space-y-0.5">
                {items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end ?? false}
                      onClick={onNavigate}
                      className={({ isActive }) => `${LINK_BASE} ${isActive ? LINK_ACTIVE : ""}`}
                    >
                      {item.icon}
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex h-16 shrink-0 items-center gap-2.5">
      <span
        aria-hidden="true"
        className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary-400 to-primary-700 text-sm font-black text-white shadow-lg shadow-primary-900/40"
      >
        B
      </span>
      <span className="text-base font-bold tracking-tight text-white">
        Balder <span className="font-medium text-gray-500">ERP</span>
      </span>
    </div>
  );
}

interface SidebarProps {
  /** Mobil çekmece açık mı. */
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const can = useCan();
  const perms: Perms = {
    isBoss: can.isBoss,
    isStaff: can.isStaff,
    seeCost: can.seeCost,
    seeFinance: can.seeFinance,
  };

  const drawerRef = useRef<HTMLDivElement>(null);

  // Kapatma geri çağrısı ref'te tutulur — kimliği değişince etki yeniden çalışmasın.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Mobil çekmece açıkken Esc kapatsın (tablette harici klavye kullanılıyor).
  // Bağımlılık yalnızca `open`: aksi hâlde her render'da `drawerRef.focus()`
  // yeniden çalışıp sayfadaki odağı çalardı (Modal'daki odak kaybı hatasının eşi).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    drawerRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      {/* Mobil / tablet çekmece */}
      <div className={`noprint relative z-50 lg:hidden ${open ? "" : "pointer-events-none"}`}>
        <div
          className={`fixed inset-0 bg-gray-900/80 transition-opacity ${open ? "opacity-100" : "opacity-0"}`}
          onClick={onClose}
          aria-hidden="true"
        />
        <div
          ref={drawerRef}
          tabIndex={-1}
          /**
           * ★ KAPALIYKEN NE `dialog` NE ODAKLANABİLİR.
           *
           * Çekmece kapalıyken yalnızca ekranın dışına kaydırılıyordu
           * (`-translate-x-full`). Ekran dışına taşınan öğe hâlâ DOM'da ve hâlâ
           * odaklanabilir: Tab'a basan kullanıcı, görünürde hiçbir şey yokken
           * menünün içindeki bağlantıları tek tek geziyor ve odağın nereye
           * gittiğini göremiyordu. `role="dialog"` + `aria-modal` da sürekli açık
           * durduğu için ekran okuyucu sayfanın geri kalanını "modal arkası"
           * sayıyordu.
           *
           * `invisible` öğeyi hem görünürden hem odak ağacından çıkarır ve
           * `transition-transform`u bozmaz (geçiş bitince uygulanır).
           */
          {...(open
            ? { role: "dialog" as const, "aria-modal": true }
            : { "aria-hidden": true })}
          aria-label="Menü"
          className={`fixed inset-y-0 left-0 flex w-72 max-w-[85%] flex-col gap-y-5 overflow-y-auto bg-gray-950 px-5 pb-4 transition-transform duration-200 ${
            open ? "translate-x-0" : "invisible -translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between">
            <Brand />
            <button
              type="button"
              onClick={onClose}
              className="-m-2.5 rounded-md p-2.5 text-gray-400 hover:text-white"
              aria-label="Menüyü kapat"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <NavList perms={perms} onNavigate={onClose} />
        </div>
      </div>

      {/* Masaüstü sabit menü */}
      <div className="noprint hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-white/5 bg-gray-950 px-5 pb-4">
          <Brand />
          <NavList perms={perms} />
        </div>
      </div>
    </>
  );
}

export default Sidebar;

/**
 * Komut paleti (Ctrl+K) için menüdeki GÖRÜNÜR sayfalar. Yetki süzgeci menüyle
 * AYNIDIR — palet, menüde olmayan bir sayfayı önermez.
 */
export function useNavItems(): Array<{ to: string; label: string; section: string; icon: ReactNode }> {
  const can = useCan();
  const perms: Perms = {
    isBoss: can.isBoss,
    isStaff: can.isStaff,
    seeCost: can.seeCost,
    seeFinance: can.seeFinance,
  };
  return SECTIONS.flatMap((s) =>
    s.items
      .filter((it) => it.visible(perms))
      .map((it) => ({ to: it.to, label: it.label, section: s.title ?? "Genel", icon: it.icon }))
  );
}
